import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PubNub from 'pubnub';
import NavBarBottom from '../../component/navigation/NavBarBottom';
import './_home.scss';
import ChatBox from '../chat/ChatBox';
import { useParams } from 'react-router-dom';
import Timer from '../../component/timer/Timer';
import {
    ResponseObject,
    TimerFormat,
    Features,
    Contact,
} from '../../interface/interface';

import api from '../../api';
import { LoadingScreen } from '../loadingScreen/LoadingScreen';
import NoPage from '../nopage/NoPage';
import uniqid from 'uniqid';
import { v4 } from 'uuid';
import { IncidentEvent } from '../../data/IncidentEvent';
import { usePubnubMessages } from '../../hooks/usePubnubMessages';

function Home() {
    const { tokenId } = useParams();
    const [uniqueActiveUsers, setUniqueActiveUsers] = useState<any>([]);
    const [cancelAlarm, setCancelAlarm] = useState<boolean>(false);
    const [cancelAlarmActive, setCancelAlarmActive] = useState<boolean>(false);
    const [requestPolice, setRequestPolice] = useState<boolean>(false);
    const [buttonBlockDisable, setButtonBlockDisable] =
        useState<boolean>(false);
    const [requestPoliceActive, setRequestPoliceActive] =
        useState<boolean>(false);
    const [updateTimer, setUpdateTimer] = useState<boolean>(false);
    const [timerDeactivated, setTimerDeactivated] = useState<boolean>(false);
    const [responseData, setResponseData] = useState<ResponseObject>();
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [timerData, setTimerData] = useState<TimerFormat>({
        csReleaseAt: new Date(),
        zeroDelay: false,
        timeExtended: false,
        timeToExtend: 0,
    });
    const [incidentEvent, setIncidentEvent] = useState<string>('');
    const [showButtonTimerSection, setShowButtonTimerSection] =
        useState<boolean>(false);
    const [showButtonSection, setShowButtonSection] = useState<boolean>(false);
    const [features, setFeatures] = useState<Features>({});
    // Dynamic height calculation
    const dynamicElementRef = useRef<HTMLDivElement | null>(null);

    const currentUserId = responseData?.contacts.find(
        (d: Contact) => d.token === tokenId,
    )?.token;

    const pubnub = useMemo(() => {
        if (!responseData || !currentUserId) {
            return;
        }
        return new PubNub({
            publishKey: responseData.pn_publish_key,
            subscribeKey: responseData.pn_subscribe_key,
            uuid: currentUserId,
            autoNetworkDetection: true,
            restore: true,
        });
    }, [responseData, currentUserId]);

    const cancelAlarmActiveHandler = useCallback(() => {
        setCancelAlarmActive(true);
        setRequestPolice(false);
        setCancelAlarm(false);
    }, []);

    const reqPolActive = useCallback(() => {
        setRequestPoliceActive(true);
        setCancelAlarm(false);
        setRequestPolice(false);
    }, []);

    const handleIncidentEvent = useCallback(
        (event: string) => {
            setIncidentEvent(event);
            setTimerDeactivated(true);

            if (
                !features['775'] ||
                (features['775'] &&
                    ['cs999', 'cso999', 'cso005'].includes(event))
            ) {
                setShowButtonSection(false);
            }
        },
        [features],
    );

    const { rawMessages, messages, sendMessage } = usePubnubMessages(
        pubnub,
        responseData?.pn_channels_to_subscribe,
        cancelAlarmActiveHandler,
        reqPolActive,
        handleIncidentEvent,
    );

    useEffect(() => {
        const allUsers = rawMessages
            .filter((message) => {
                return message?.message?.payload?.type === 'eu001';
            })
            .map((message) => {
                return message?.message?.payload?.sender?.name;
            });
        setUniqueActiveUsers([...new Set(allUsers)]);
    }, [rawMessages]);

    const cancelOrPoliceAlreadySubmitted = rawMessages.find((message) => {
        return ['eu006', 'eu005'].includes(message?.message?.type);
    });

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                window.location.reload();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, []);

    useEffect(() => {
        setLoading(true);
        const storedToken = localStorage.getItem('api_Token');
        const storedTenantId = localStorage.getItem('tenant_id');
        if (storedToken) {
            api.get(`${storedTenantId}/incident`, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                    correlationId: v4(),
                },
            })
                .then((response) => {
                    setIsError(false);
                    setResponseData(response.data);
                    setTimerData({
                        csReleaseAt: new Date(response.data.cs_release_at),
                        zeroDelay: response.data.zero_delay_customer,
                        timeExtended: response.data.time_extended,
                        timeToExtend: response.data.time_to_extend,
                    });
                    setFeatures(response.data.features || {});
                })
                .catch((_) => {
                    setIsError(true);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, []);

    const cancelAlarmHandler = () => {
        setCancelAlarm(!cancelAlarm);
    };

    const requestPoliceHandler = (getValue: boolean) => {
        setRequestPolice(getValue);
    };

    const buttonBlockDisableHandler = (getValue: boolean) => {
        setButtonBlockDisable(getValue);
    };

    const isTimerUpdate = () => {
        setUpdateTimer(true);
    };

    const handleTimerEnd = () => {
        setTimerDeactivated(true);
        setCancelAlarm(false);
        setRequestPolice(false);
    };

    let shieldIcon = null;
    let text = null;

    useEffect(() => {
        const latestStatusType = responseData?.latest_status.type!;

        setShowButtonTimerSection(false);
        if (
            [
                'TIME_EXPIRED',
                'cs001',
                'cs999',
                'cso001',
                'cso999',
                'cso005',
            ].includes(latestStatusType)
        ) {
            handleIncidentEvent(latestStatusType);
        }

        if (latestStatusType === 'eu005') {
            cancelAlarmActiveHandler();
        }

        if (latestStatusType === 'eu006') {
            reqPolActive();
        }

        if (!features['775']) {
            if (
                [
                    'cs001',
                    'cso001',
                    'cs999',
                    'cso999',
                    'cso005',
                    'eu005',
                    'eu006',
                    'TIME_EXPIRED',
                ].includes(latestStatusType)
            ) {
                setShowButtonTimerSection(false);
                setShowButtonSection(false);
                if (
                    ['TIME_EXPIRED'].includes(latestStatusType) &&
                    timerData.zeroDelay
                ) {
                    setShowButtonSection(true);
                }
            } else {
                setShowButtonTimerSection(true);
                setShowButtonSection(true);
            }
        } else if (
            ['cs999', 'cso999', 'cso005', 'eu005', 'eu006'].includes(
                latestStatusType,
            )
        ) {
            setShowButtonTimerSection(false);
            setShowButtonSection(false);
        } else if (
            ['TIME_EXPIRED', 'cs001', 'cso001'].includes(latestStatusType)
        ) {
            setShowButtonTimerSection(false);
            setShowButtonSection(true);
        } else {
            setShowButtonTimerSection(true);
            setShowButtonSection(true);
        }
    }, [
        responseData,
        timerData.zeroDelay,
        features,
        handleIncidentEvent,
        cancelAlarmActiveHandler,
        reqPolActive,
    ]);

    const isAgentProcessEvents = ['cs001', 'cso001'].includes(incidentEvent);

    if (
        (incidentEvent &&
            IncidentEvent[incidentEvent] !== IncidentEvent.TIME_EXPIRED &&
            timerData.zeroDelay) ||
        (incidentEvent && !timerData.zeroDelay)
    ) {
        shieldIcon = IncidentEvent[incidentEvent].shieldIcon;
        if (incidentEvent === 'eu005') {
            text = (
                <p
                    className="p3 text-uppercase"
                    data-testid="cancel-alarm-text"
                >
                    {IncidentEvent[incidentEvent].message}
                </p>
            );
        } else {
            text = (
                <p className="p1 text-uppercase">
                    {IncidentEvent[incidentEvent].message}
                </p>
            );
        }
    } else if (
        !timerDeactivated ||
        timerData.zeroDelay ||
        (features['775'] && timerDeactivated)
    ) {
        if (cancelAlarmActive) {
            shieldIcon = IncidentEvent['eu005'].shieldIcon;
            text = (
                <p
                    className="p3 text-uppercase"
                    data-testid="cancel-alarm-text"
                >
                    {IncidentEvent['eu005'].message}
                </p>
            );
        } else if (requestPoliceActive) {
            shieldIcon = IncidentEvent['eu006'].shieldIcon;
            text = (
                <p className="p1 text-uppercase">
                    {IncidentEvent['eu006'].message}
                </p>
            );
        } else if (isAgentProcessEvents) {
            shieldIcon = IncidentEvent[incidentEvent].shieldIcon;
            text = (
                <p className="p1 text-uppercase">
                    {IncidentEvent[incidentEvent].message}
                </p>
            );
        } else if (timerDeactivated && !timerData.zeroDelay) {
            shieldIcon = IncidentEvent['TIME_EXPIRED'].shieldIcon;
            text = (
                <p className="p1 text-uppercase">
                    {IncidentEvent['TIME_EXPIRED'].message}
                </p>
            );
        } else if (!cancelAlarmActive && !requestPoliceActive) {
            shieldIcon = IncidentEvent['BURGLAR_ALARM_TRIGGERED'].shieldIcon;
            text = (
                <p className="p1 text-uppercase">
                    {IncidentEvent['BURGLAR_ALARM_TRIGGERED'].message}
                </p>
            );
        }
    } else {
        shieldIcon = IncidentEvent['TIME_EXPIRED'].shieldIcon;
        text = (
            <p className="p1 text-uppercase">
                {IncidentEvent['TIME_EXPIRED'].message}
            </p>
        );
    }

    useEffect(() => {
        if (
            (timerDeactivated || cancelAlarmActive || requestPoliceActive) &&
            !timerData.zeroDelay
        ) {
            setShowButtonTimerSection(false);
            if (
                !features['775'] ||
                (features['775'] &&
                    !timerData.zeroDelay &&
                    (cancelAlarmActive || requestPoliceActive))
            ) {
                setShowButtonSection(false);
            }
        }
    }, [
        timerDeactivated,
        cancelAlarmActive,
        requestPoliceActive,
        timerData.zeroDelay,
        features,
    ]);

    return (
        <>
            {loading ? (
                <LoadingScreen />
            ) : (
                <>
                    {isError ? (
                        <>
                            <NoPage />
                            <NavBarBottom />
                        </>
                    ) : (
                        <>
                            <div className="top-section text-left">
                                <div className="info-box">
                                    <div className="row">
                                        <div className="col-10">
                                            <div className="d-flex text-left add-margin-left">
                                                <div className="block-left">
                                                    {shieldIcon}
                                                </div>
                                                <div className="block-right">
                                                    {text}
                                                    <p className="p2">
                                                        {
                                                            responseData
                                                                ?.address
                                                                .address1
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-2 text-right">
                                            <div className="name-icon text-center position-relative">
                                                {uniqueActiveUsers.map(
                                                    (d: any, i: any) => {
                                                        return (
                                                            <p
                                                                key={uniqid()}
                                                                style={{
                                                                    right: `${
                                                                        19 * i
                                                                    }px`,
                                                                }}
                                                            >
                                                                {d?.charAt(0)}
                                                            </p>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                ref={dynamicElementRef}
                                className="conditionally-hide-block"
                            >
                                {showButtonSection &&
                                    !cancelOrPoliceAlreadySubmitted && (
                                        <div
                                            className="button-block container"
                                            key={'dBlock-1'}
                                        >
                                            <div className="row add-padding">
                                                <div className="col d-grid">
                                                    <button
                                                        className="btn btn-green"
                                                        type="button"
                                                        disabled={
                                                            cancelAlarm ||
                                                            cancelAlarmActive ||
                                                            requestPoliceActive ||
                                                            requestPolice ||
                                                            buttonBlockDisable
                                                        }
                                                        onClick={
                                                            cancelAlarmHandler
                                                        }
                                                        data-testid="cancel-alarm-button"
                                                    >
                                                        Cancel alarm
                                                    </button>
                                                </div>
                                                <div className="col d-grid">
                                                    <button
                                                        className="btn btn-red"
                                                        type="button"
                                                        disabled={
                                                            cancelAlarm ||
                                                            cancelAlarmActive ||
                                                            requestPoliceActive ||
                                                            requestPolice ||
                                                            buttonBlockDisable
                                                        }
                                                        onClick={() =>
                                                            requestPoliceHandler(
                                                                true,
                                                            )
                                                        }
                                                        data-testid="request-police-button"
                                                    >
                                                        Request police
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                {showButtonTimerSection && (
                                    <div className="timer-bar" key={'dBlock-2'}>
                                        <Timer
                                            isTimerUpdate={isTimerUpdate}
                                            onTimerEnd={handleTimerEnd}
                                            timerData={timerData}
                                            updateTimer={updateTimer}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="agents-message-bar">
                                <p className="about-chat">
                                    ADT Agents cannot view this chat session
                                </p>
                            </div>

                            <ChatBox
                                cancelAlarm={cancelAlarm}
                                cancelAlarmHandler={cancelAlarmHandler}
                                currentUserId={currentUserId}
                                requestPolice={requestPolice}
                                requestPoliceHandler={requestPoliceHandler}
                                cancelAlarmActiveHandler={
                                    cancelAlarmActiveHandler
                                }
                                reqPolActive={reqPolActive}
                                updateTimer={updateTimer}
                                responseData={responseData}
                                buttonBlockDisableHandler={
                                    buttonBlockDisableHandler
                                }
                                handleIncidentEvent={handleIncidentEvent}
                                setShowButtonSection={setShowButtonSection}
                                timerData={timerData}
                                messages={messages}
                                sendMessage={sendMessage}
                            />

                            <NavBarBottom resetNavPosition />
                        </>
                    )}
                </>
            )}
        </>
    );
}

export default Home;
